<template>
  <div
    class="number-input"
    :class="{
      'number-input--inline': inline,
      'number-input--center': center,
      'number-input--controls': controls,
    }"
    v-on="listeners"
  >
    <button
      v-if="controls"
      class="number-input__button number-input__button--minus"
      type="button"
      tabindex="-1"
      :disabled="disabled || readonly || !decreasable"
      @click="decrease"
    >
      <font-awesome-icon :icon="['fas', 'minus']" fixed-width />
    </button>
    <input
      ref="input"
      class="fts-form-control number-input__input"
      :class="{'form-control--error': error}"
      v-bind="attrs"
      type="number"
      :name="name"
      :value="currentValue"
      :min="min"
      :max="max"
      :step="step"
      :readonly="readonly || !inputtable"
      :disabled="disabled || (!decreasable && !increasable)"
      :placeholder="placeholder"
      autocomplete="off"
      @input="change"
      @blur="onBlur"
      @paste="paste"
      @keypress="onKeypress"
    >
    <button
      v-if="controls"
      class="number-input__button number-input__button--plus"
      type="button"
      tabindex="-1"
      :disabled="disabled || readonly || !increasable"
      @click="increase"
    >
      <font-awesome-icon :icon="['fas', 'plus']" fixed-width />
    </button>
  </div>
</template>

<script>
import commons from '../commons/commons';

const isNaN = Number.isNaN || window.isNaN;
const REGEXP_NUMBER = /^-?(?:\d+|\d+\.\d+|\.\d+)(?:[eE][-+]?\d+)?$/;
const REGEXP_DECIMALS = /\.\d*(?:0|9){10}\d*$/;
const normalizeDecimalNumber = (value, times = 100000000000) => (
  REGEXP_DECIMALS.test(value) ? (Math.round(value * times) / times) : value
);

export default {
  model: {
    event: 'change',
  },
  props: {
    attrs: {
      type: Object,
      default: undefined,
    },
    center: Boolean,
    controls: Boolean,
    disabled: Boolean,
    inputtable: {
      type: Boolean,
      default: true,
    },
    inline: Boolean,
    max: {
      type: Number,
      default: Infinity,
    },
    min: {
      type: Number,
      default: -Infinity,
    },
    name: {
      type: String,
      default: undefined,
    },
    placeholder: {
      type: String,
      default: undefined,
    },
    readonly: Boolean,
    rounded: Boolean,
    step: {
      type: Number,
      default: 1,
    },
    value: {
      type: Number,
      default: null, /* default NaN */
    },
    error: Boolean
  },
  data() {
    return {
      currentValue: null, /* default NaN */
      inputValue: null, /* refer onKeypress (prevent keyCode 45) */
    };
  },
  computed: {
    /**
     * Indicate if the value is increasable.
     * @returns {boolean} Return `true` if it is decreasable, else `false`.
     */
    increasable() {
      const num = this.currentValue;

      return isNaN(num) || num < this.max;
    },
    /**
     * Indicate if the value is decreasable.
     * @returns {boolean} Return `true` if it is decreasable, else `false`.
     */
    decreasable() {
      const num = this.currentValue;

      return isNaN(num) || num > this.min;
    },
    /**
     * Filter listeners
     * @returns {Object} Return filtered listeners.
     */
    listeners() {
      const listeners = { ...this.$listeners };

      delete listeners.change;

      return listeners;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue, oldValue) {
        if (
          // Avoid triggering change event when created
          !(isNaN(newValue) && typeof oldValue === 'undefined')

          // Avoid infinite loop
          && newValue !== this.currentValue
        ) {
          this.setValue(newValue);
        }
      },
    },
  },
  methods: {
    /**
     * Change event handler.
     * @param {string} value - The new value.
     */
    change(event) {
      if (commons.isNumber(event.target.value)) 
        this.setValue(Math.min(this.max, Math.max(this.min, event.target.value)));
      else 
        this.setValue(event.target.value || event.data);
    },
    /**
     * Paste event handler.
     * @param {Event} event - Event object.
     */
    paste(event) {
      const clipboardData = event.clipboardData || window.clipboardData;

      if (clipboardData && !REGEXP_NUMBER.test(clipboardData.getData('text'))) {
        event.preventDefault();
      }
    },
    /* keypress event */
    onKeypress(event) {
      const keyCode = event.keyCode;
      let preventDefault = true;
      /* 45 => -, 46 => . */
      if (
        (
          keyCode === 45 && 
          (
            /* check is allow negative */
            !commons.isNumber(this.min) || 
            commons.isNumber(this.min) && parseFloat(this.min) < 0 
          ) &&
          (
            /* prevent keyCode 45 */
            !(this.inputValue && this.inputValue.length) ||
            (
              this.inputValue && this.inputValue.length === window.getSelection().toString().length
            )
          )
        ) || 
        /* check is allow decimal */
        (keyCode === 46 && !this.rounded)
      ) {
        preventDefault = false;
      }

      if (preventDefault && (keyCode < 48 || keyCode > 57)) {
        event.preventDefault();
      }
    },
    /**
     * Decrease the value.
     */
    decrease() {
      if (this.decreasable) {
        let { currentValue } = this;

        if (isNaN(currentValue)) {
          currentValue = 0;
        }

        this.setValue(Math.min(this.max, Math.max(
          this.min,
          normalizeDecimalNumber(currentValue - this.step),
        )));
      }
    },
    /**
     * Increase the value.
     */
    increase() {
      if (this.increasable) {
        let { currentValue } = this;

        if (isNaN(currentValue)) {
          currentValue = 0;
        }

        this.setValue(Math.min(this.max, Math.max(
          this.min,
          normalizeDecimalNumber(currentValue + this.step),
        )));
      }
    },
    /**
     * Set new value and dispatch change event.
     * @param {number} value - The new value to set.
     */
    setValue(value) {
      !isNaN(value) && (this.inputValue = commons.isNumber(value) || value === '-' ? value.toString() : null);
      
      const oldValue = this.currentValue;
      let newValue = null;
      if (value || commons.isNumber(value)) {
        newValue = this.rounded ? Math.round(value) : value;

        if (this.min <= this.max) {
          newValue = Math.min(this.max, Math.max(this.min, newValue));
        }
      }

      this.currentValue = newValue;

      if (newValue === oldValue) {
        // Force to override the number in the input box (#13).
        this.$refs.input.value = newValue;
      }

      this.$emit('change', newValue, oldValue);
    },
    onBlur(e) {
      try {
        if (!this.$el.contains(e.relatedTarget)) this.$emit("blur");
      } catch(err) {
        console.warn('[DOMException]', err);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.number-input {
  display: block;
  max-width: 100%;
  position: relative;

  &__button {
    border: 0;
    bottom: 1px;
    position: absolute;
    top: 1px;
    width: 2.5rem;
    background: transparent;
    color: var(--fts-form-label);
    z-index: 1;

    &:focus {
      outline: none;
    }

    &:hover {
      cursor: pointer;
      color: var(--fts-primary);
    }

    &:disabled {
      opacity: 0.65;
    }

    &--minus {
      left: 1px;
    }

    &--plus {
      right: 1px;
    }
  }

  &__input {
    -moz-appearance: textfield;
  }

  &--inline {
    display: inline-block;

    & > input {
      display: inline-block;
    }
  }

  &--center {
    & > input {
      text-align: center;
    }
  }

  &--controls {
    & > input {
      padding-left: 3.375rem;
      padding-right: 3.375rem;
    }
  }
}
</style>